import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./db-models').NoSQLEventReaction } NoSQLEventReaction
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The event reactions collection access functions
 */
export default function reactionsCollectionFns(db) {
  /**
   * Add a reaction to an event
   * @param {string} teamId The team id
   * @param {string} eventId The event id
   * @param {string} userId The user adding the reaction
   * @param {string} emoji The emoji character
   * @returns {Promise<NoSQLEventReaction>} The created reaction
   */
  async function addReaction(teamId, eventId, userId, emoji) {
    try {
      const reactionsRef = collection(
        db,
        Collections.TEAMS,
        teamId,
        "events",
        eventId,
        "reactions",
      );
      const docRef = await addDoc(reactionsRef, {
        userId,
        emoji,
        createdAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        userId,
        emoji,
        createdAt: new Date(),
      };
    } catch (error) {
      console.error("Failed to add reaction:", error);
      throw error;
    }
  }

  /**
   * Remove a reaction from an event
   * @param {string} teamId The team id
   * @param {string} eventId The event id
   * @param {string} reactionId The reaction id to remove
   * @returns {Promise<void>}
   */
  async function removeReaction(teamId, eventId, reactionId) {
    try {
      const reactionRef = doc(
        db,
        Collections.TEAMS,
        teamId,
        "events",
        eventId,
        "reactions",
        reactionId,
      );
      await deleteDoc(reactionRef);
    } catch (error) {
      console.error("Failed to remove reaction:", error);
      throw error;
    }
  }

  /**
   * Find a user's reaction with a specific emoji on an event
   * @param {string} teamId The team id
   * @param {string} eventId The event id
   * @param {string} userId The user id
   * @param {string} emoji The emoji character
   * @returns {Promise<NoSQLEventReaction|null>} The reaction if found
   */
  async function findUserReaction(teamId, eventId, userId, emoji) {
    try {
      const reactionsRef = collection(
        db,
        Collections.TEAMS,
        teamId,
        "events",
        eventId,
        "reactions",
      );
      const q = query(
        reactionsRef,
        where("userId", "==", userId),
        where("emoji", "==", emoji),
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const docSnap = snapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    } catch (error) {
      console.error("Failed to find user reaction:", error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for event reactions
   * @param {string} teamId The team id
   * @param {string} eventId The event id
   * @param {function(NoSQLEventReaction[]): void} callback Function to call with updated reactions
   * @returns {function(): void} Unsubscribe function
   */
  function subscribeToEventReactions(teamId, eventId, callback) {
    try {
      const reactionsRef = collection(
        db,
        Collections.TEAMS,
        teamId,
        "events",
        eventId,
        "reactions",
      );

      return onSnapshot(
        reactionsRef,
        (snapshot) => {
          const reactions = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          callback(reactions);
        },
        (error) => {
          console.error("Error in reactions subscription:", error);
        },
      );
    } catch (error) {
      console.error("Failed to subscribe to reactions:", error);
      throw error;
    }
  }

  return {
    addReaction,
    removeReaction,
    findUserReaction,
    subscribeToEventReactions,
  };
}
